import type { Scene } from "@core/scene-manager";
import type { Tick } from "@core/clock";
import type { EventBus, GameEvents } from "@core/event-bus";
import type { GameState } from "@core/store";
import type { Seconds } from "@core/types";
import { asPercent, asSeconds, asEntityId } from "@core/types";
import type { RenderTargets, DomRenderer } from "@rendering/dom-renderer";
import type { AnimationController } from "@rendering/animation-controller";
import type { Platform } from "@platform/index";
import { LEVEL_MOONLEAF } from "@data/levels";
import { CALM_SPOT_POSITION, CALM_SPOT_RADIUS, TOTAL_SEEDS } from "@data/balance";
import { createPlayer } from "@entities/player";
import { createSeed, createSand, createFlower, createGlowfly } from "@entities/collectible";
import type { Collectible } from "@entities/collectible";
import { GLOWFLY_ORBIT_RADIUS, GLOWFLY_ORBIT_SPEED } from "@data/balance";
import { applyMovement } from "@systems/movement-system";
import { updateEnergy } from "@systems/energy-system";
import { checkCollections } from "@systems/collection-system";
import { checkCompletion } from "@systems/completion-system";
import {
  KeyboardInputSource,
  JoystickInputSource,
  GlowButtonInputSource,
  CompositeInputSource,
} from "@systems/input-system";
import type { Player } from "@entities/player";

/** Позиции bloom-burst точек для финальной анимации. */
const BLOOM_POINTS = [
  { x: 43, y: 45 },
  { x: 50, y: 45 },
  { x: 57, y: 46 },
  { x: 42, y: 55 },
  { x: 50, y: 53 },
  { x: 58, y: 56 },
  { x: 31, y: 62 },
  { x: 69, y: 61 },
  { x: 22, y: 36 },
  { x: 82, y: 38 },
  { x: 63, y: 77 },
  { x: 17, y: 78 },
] as const;

const PARTICLE_INTERVAL: Seconds = asSeconds(0.12);

/** Тёплые приветствия Boo при сборе семечка (ещё не все собраны). */
const SEED_MESSAGES = [
  "A Moonleaf Seed is safe with Boo.",
  "Wonderful! Boo found one.",
  "Such a quiet little treasure.",
  "Boo glows a little brighter.",
  "Tucked safely under Boo's scarf.",
] as const;

/** Сообщения когда заряжаемся песком. */
const SAND_MESSAGES = [
  "Glow Sand warmed the hourglass.",
  "Boo's lantern hums with new light.",
  "A little spark for the journey.",
  "The sand twinkles, then settles.",
] as const;

/** Сообщения когда лечимся цветком. */
const FLOWER_MESSAGES = [
  "The Glow Flower shared its light.",
  "Petals open, and Boo smiles.",
  "A bright bloom for a brave ghost.",
] as const;

/** Сообщения при поимке glowfly. */
const GLOWFLY_MESSAGES = [
  "A glowfly danced into Boo's lantern.",
  "Whoosh! A tiny new friend.",
  "Boo giggles in golden light.",
] as const;

const pickRandom = <T>(items: readonly T[]): T => {
  // safe: items is non-empty in our usage
  const idx = Math.floor(Math.random() * items.length);
  return items[idx] ?? items[0]!;
};

/** Зависимости GameScene, передаются через DI. */
export interface GameSceneDeps {
  targets: RenderTargets;
  renderer: DomRenderer;
  state: GameState;
  bus: EventBus<GameEvents>;
  platform: Platform;
  animation?: AnimationController;
}

/** Основная игровая сцена. Оркестрирует все системы на каждый тик. */
export class GameScene implements Scene {
  readonly id = "game" as const;

  private readonly renderer: DomRenderer;
  private readonly state: GameState;
  private readonly bus: EventBus<GameEvents>;
  private readonly targets: RenderTargets;
  private readonly animation: AnimationController | undefined;
  private readonly platform: Platform;

  private player!: Player;
  private items: Collectible[] = [];
  private glow = false;
  private complete = false;
  private paused = false;
  private seedsCollected = 0;
  private glowfliesCollected = 0;
  private totalGlowflies = 0;
  private particleTimer: Seconds = asSeconds(0);
  private calmPulseTimer: Seconds = asSeconds(0);
  private inputSource!: CompositeInputSource;

  constructor(deps: GameSceneDeps) {
    this.renderer = deps.renderer;
    this.state = deps.state;
    this.bus = deps.bus;
    this.targets = deps.targets;
    this.animation = deps.animation;
    this.platform = deps.platform;
  }

  /** Инициализирует сцену: создаёт игрока, предметы, input-источники. */
  onEnter(): void {
    this.player = createPlayer(LEVEL_MOONLEAF.playerStart);
    this.items = [
      ...LEVEL_MOONLEAF.seeds.map((s) => createSeed(s.id, s.position)),
      ...LEVEL_MOONLEAF.sand.map((s) => createSand(s.id, s.position)),
      ...LEVEL_MOONLEAF.flowers.map((f) => createFlower(f.id, f.position)),
      ...LEVEL_MOONLEAF.glowflies.map((g) => createGlowfly(g.id, g.position)),
    ];
    this.glow = false;
    this.complete = false;
    this.paused = false;
    this.seedsCollected = 0;
    this.glowfliesCollected = 0;
    this.totalGlowflies = LEVEL_MOONLEAF.glowflies.length;
    this.particleTimer = asSeconds(0);
    this.calmPulseTimer = asSeconds(0);

    // Инициализируем input
    this.inputSource = new CompositeInputSource([
      new KeyboardInputSource(),
      new JoystickInputSource(this.targets.joystickBase, this.targets.joystickKnob),
      new GlowButtonInputSource(this.targets.glowButton),
    ]);

    // Монтируем collectibles
    this.renderer.renderCollectibles(this.items, this.player.position, false);

    // Рисуем начальное состояние
    this.renderer.renderEnergy(this.state.energy.peek());
    this.renderer.renderSeeds(0, TOTAL_SEEDS);
    this.renderer.renderGlowflies(0, this.totalGlowflies);
    this.renderer.renderMessage("Find 5 Moonleaf Seeds.");
    this.renderer.renderPlayer(this.player, 0);
  }

  /** Освобождает ресурсы при выходе из сцены. */
  onExit(): void {
    this.inputSource.dispose();
  }

  /**
   * Сбрасывает игровое состояние к начальному.
   * Вызывается перед повторным входом из bloom-сцены.
   */
  reset(): void {
    this.player = createPlayer(LEVEL_MOONLEAF.playerStart);
    this.items = [
      ...LEVEL_MOONLEAF.seeds.map((s) => createSeed(s.id, s.position)),
      ...LEVEL_MOONLEAF.sand.map((s) => createSand(s.id, s.position)),
      ...LEVEL_MOONLEAF.flowers.map((f) => createFlower(f.id, f.position)),
      ...LEVEL_MOONLEAF.glowflies.map((g) => createGlowfly(g.id, g.position)),
    ];
    this.glow = false;
    this.complete = false;
    this.seedsCollected = 0;
    this.glowfliesCollected = 0;
    this.totalGlowflies = LEVEL_MOONLEAF.glowflies.length;
    this.particleTimer = asSeconds(0);
    this.calmPulseTimer = asSeconds(0);

    // Сбрасываем глобальное состояние
    this.state.energy.set(asPercent(100));
    this.state.seedsCollected.set(0);
    this.state.complete.set(false);
    this.state.glow.set(false);

    // Очищаем DOM-элементы celebration
    const celebration = this.targets.celebration;
    while (celebration.firstChild) celebration.removeChild(celebration.firstChild);
    // Размонтируем все collectible ноды для чистого переинициализации
    this.renderer.unmountAllCollectibles();
    this.renderer.setStageClass("bloomed", false);
    this.renderer.setGlowState(false);
  }

  /**
   * Ставит игру на паузу: онбордингу/паузному оверлею это нужно, чтобы
   * glow не тратился и Boo не двигался, пока окно показано.
   */
  pause(): void {
    this.paused = true;
    if (this.glow) {
      this.glow = false;
      this.renderer.setGlowState(false);
      this.state.glow.set(false);
      this.animation?.glowToggle(false);
    }
  }

  /** Снимает паузу. */
  resume(): void {
    this.paused = false;
  }

  /** Основной игровой тик: input → движение → энергия → сбор → частицы → завершение → рендер. */
  onUpdate(ctx: Tick): void {
    if (this.complete || this.paused) return;

    const { dt, elapsed } = ctx;
    const elapsedMs = (elapsed as number) * 1000;

    // 1. Input
    const input = this.inputSource.poll();

    // 2. Glow state
    const wantsGlow = input.glowPressed;
    const currentEnergy = this.state.energy.peek();
    const newGlow = wantsGlow && (currentEnergy as number) > 0;

    if (newGlow !== this.glow) {
      this.glow = newGlow;
      this.renderer.setGlowState(this.glow);
      this.state.glow.set(this.glow);
      this.animation?.glowToggle(this.glow);
    }

    // 3. Movement
    applyMovement(this.player, input.axis, this.glow, dt);
    this.state.player.set(this.player.position);

    // 4. Energy
    const pos = this.player.position;
    const inCalmSpot =
      Math.hypot(pos.x - CALM_SPOT_POSITION.x, pos.y - CALM_SPOT_POSITION.y) < CALM_SPOT_RADIUS;

    const energyResult = updateEnergy({
      energy: this.state.energy.peek(),
      glow: this.glow,
      inCalmSpot,
      dt,
    });
    this.state.energy.set(energyResult.energy);
    this.renderer.renderEnergy(energyResult.energy);

    if (energyResult.depletedTransition) {
      this.glow = false;
      this.renderer.setGlowState(false);
      this.state.glow.set(false);
      this.renderer.renderMessage(
        "The hourglass is quiet. Find Glow Sand, flowers, or the calm circle.",
      );
      this.bus.emit("energy:depleted", null);
    }

    if (energyResult.fullTransition) {
      this.bus.emit("energy:full", null);
    }

    // Calm spot сообщение раз в 0.8 секунды
    if (inCalmSpot && (currentEnergy as number) < 100) {
      this.calmPulseTimer = asSeconds((this.calmPulseTimer as number) + (dt as number));
      if ((this.calmPulseTimer as number) > 0.8) {
        this.renderer.renderMessage("The calm circle is refilling Boo's light.");
        this.calmPulseTimer = asSeconds(0);
      }
    } else {
      this.calmPulseTimer = asSeconds(0);
    }

    // 4b. Glowfly orbit — обновляем position для каждого живого glowfly
    for (const item of this.items) {
      if (item.type !== "glowfly" || item.collected) continue;
      item.phase += GLOWFLY_ORBIT_SPEED * (dt as number);
      item.position.x = item.basePosition.x + Math.cos(item.phase) * GLOWFLY_ORBIT_RADIUS;
      item.position.y = item.basePosition.y + Math.sin(item.phase) * (GLOWFLY_ORBIT_RADIUS * 0.6);
    }

    // 5. Collection
    const effects = checkCollections(this.player.position, this.glow, this.items);
    for (const effect of effects) {
      if (effect.kind === "seed-collected") {
        this.seedsCollected += 1;
        this.state.seedsCollected.set(this.seedsCollected);
        this.renderer.renderSeeds(this.seedsCollected, TOTAL_SEEDS);
        const seedEl = this.renderer.getCollectibleNode(asEntityId(effect.id));
        if (seedEl && this.animation) void this.animation.popCollect(seedEl);
        this.renderer.playBooJoy(this.player.position);
        this.platform.haptics.impact("light");
        this.bus.emit("seed:collected", {
          id: asEntityId(effect.id),
          remaining: TOTAL_SEEDS - this.seedsCollected,
        });
        if (this.seedsCollected >= TOTAL_SEEDS) {
          this.renderer.renderMessage("Bring the seeds to the garden center.");
        } else {
          this.renderer.renderMessage(pickRandom(SEED_MESSAGES));
        }
      } else if (effect.kind === "sand-collected") {
        const prev = this.state.energy.peek();
        const newEnergy = asPercent(
          Math.min(100, (prev as number) + (effect.energyDelta as number)),
        );
        this.state.energy.set(newEnergy);
        this.renderer.renderEnergy(newEnergy);
        const sandEl = this.renderer.getCollectibleNode(asEntityId(effect.id));
        if (sandEl && this.animation) void this.animation.popCollect(sandEl);
        this.renderer.playBooJoy(this.player.position);
        this.platform.haptics.impact("light");
        this.renderer.renderMessage(pickRandom(SAND_MESSAGES));
        this.bus.emit("sand:collected", {
          id: asEntityId(effect.id),
          energyDelta: effect.energyDelta,
        });
      } else if (effect.kind === "flower-used") {
        const prev = this.state.energy.peek();
        const newEnergy = asPercent(
          Math.min(100, (prev as number) + (effect.energyDelta as number)),
        );
        this.state.energy.set(newEnergy);
        this.renderer.renderEnergy(newEnergy);
        const flowerEl = this.renderer.getCollectibleNode(asEntityId(effect.id));
        if (flowerEl && this.animation) void this.animation.popCollect(flowerEl);
        this.renderer.playBooJoy(this.player.position);
        this.platform.haptics.impact("medium");
        this.renderer.renderMessage(pickRandom(FLOWER_MESSAGES));
        this.bus.emit("flower:used", {
          id: asEntityId(effect.id),
          energyDelta: effect.energyDelta,
        });
      } else if (effect.kind === "glowfly-collected") {
        const prev = this.state.energy.peek();
        const newEnergy = asPercent(
          Math.min(100, (prev as number) + (effect.energyDelta as number)),
        );
        this.state.energy.set(newEnergy);
        this.renderer.renderEnergy(newEnergy);
        this.glowfliesCollected += 1;
        this.renderer.renderGlowflies(this.glowfliesCollected, this.totalGlowflies);
        const flyEl = this.renderer.getCollectibleNode(asEntityId(effect.id));
        if (flyEl && this.animation) void this.animation.popCollect(flyEl);
        this.renderer.playBooJoy(this.player.position);
        this.platform.haptics.impact("light");
        this.renderer.renderMessage(pickRandom(GLOWFLY_MESSAGES));
        this.bus.emit("glowfly:collected", {
          id: asEntityId(effect.id),
          energyDelta: effect.energyDelta,
        });
      }
    }

    // Обновляем рендер collectibles
    this.renderer.renderCollectibles(this.items, this.player.position, this.glow);

    // 6. Particles
    if (this.glow) {
      this.particleTimer = asSeconds((this.particleTimer as number) + (dt as number));
      if ((this.particleTimer as number) >= (PARTICLE_INTERVAL as number)) {
        this.particleTimer = asSeconds(0);
        this.renderer.spawnGlowParticle(this.player.position);
      }
    } else {
      this.particleTimer = asSeconds(0);
    }

    // 7. Completion
    const shouldComplete = checkCompletion({
      player: this.player.position,
      seedsCollected: this.seedsCollected,
      totalSeeds: TOTAL_SEEDS,
      alreadyComplete: this.complete,
    });

    if (shouldComplete) {
      this.complete = true;
      this.state.complete.set(true);
      this.glow = false;
      this.renderer.setGlowState(false);
      this.state.glow.set(false);
      this.renderer.setStageClass("bloomed", true);
      this.renderer.renderMessage("Small lights can help big things grow.");
      const bloomEls = this.renderer.spawnBloomBursts(BLOOM_POINTS);
      if (this.animation) {
        void this.animation.bloomBurst(bloomEls, { staggerMs: 70 });
      }
      this.bus.emit("level:complete", null);
    }

    // 8. Render player
    this.renderer.renderPlayer(this.player, elapsedMs);
  }
}
